import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Lock, AlertTriangle, Globe, AtSign, Sun, Moon, Monitor } from 'lucide-react';
import { DashboardLayout } from '@/components/layout';
import { Card, CardTitle, Button, Input, Modal, ModalFooter, Badge } from '@/components/ui';
import { useToast } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { usePage } from '@/hooks/usePage';
import { useTheme } from '@/hooks/useTheme';

const Settings = () => {
  const { t, i18n } = useTranslation();
  const { user, updateDisplayName, changePassword, changeEmail, deleteAccount, updateUsername } = useAuth();
  const { page, refresh: refreshPage } = usePage();
  const { theme, setTheme } = useTheme();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const isOAuth = user?.providerData.some(
    (p) => p.providerId === 'google.com' || p.providerId === 'facebook.com'
  );
  const oauthProvider = user?.providerData.find(
    (p) => p.providerId === 'google.com' || p.providerId === 'facebook.com'
  );
  const providerLabel = oauthProvider?.providerId === 'google.com' ? 'Google' : oauthProvider?.providerId === 'facebook.com' ? 'Facebook' : null;

  // Account form
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [isSavingAccount, setIsSavingAccount] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{ currentPassword?: string; newPassword?: string; confirmPassword?: string }>({});

  // Username form
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [isSavingUsername, setIsSavingUsername] = useState(false);

  // Delete account
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Current username from page (source of truth)
  const currentUsername = page?.username || user?.username || '';

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const validateUsername = (username: string): string | null => {
    if (username.length < 3) {
      return t('auth.usernameTooShort');
    }
    const usernameRegex = /^[a-zA-Z0-9_.]+$/;
    if (!usernameRegex.test(username)) {
      return t('auth.usernameInvalidChars');
    }
    return null;
  };

  const handleUsernameChange = (value: string) => {
    setNewUsername(value);
    if (value) {
      const error = validateUsername(value);
      setUsernameError(error || '');
    } else {
      setUsernameError('');
    }
  };

  const handleUpdateUsername = async () => {
    if (!newUsername) return;

    const error = validateUsername(newUsername);
    if (error) {
      setUsernameError(error);
      return;
    }

    if (newUsername.toLowerCase() === currentUsername.toLowerCase()) {
      setUsernameError('');
      setNewUsername('');
      return;
    }

    setIsSavingUsername(true);
    try {
      await updateUsername(newUsername);
      await refreshPage();
      setNewUsername('');
      setUsernameError('');
      addToast({ type: 'success', title: t('settings.usernameUpdated'), message: t('settings.usernameUpdatedMessage') });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('settings.updateFailed');
      setUsernameError(message);
      addToast({ type: 'error', title: t('settings.updateFailed'), message });
    } finally {
      setIsSavingUsername(false);
    }
  };

  const handleSaveAccount = async () => {
    if (!user) return;

    setIsSavingAccount(true);
    try {
      const nameChanged = displayName !== (user.displayName || '');
      const emailChanged = email !== (user.email || '');

      if (emailChanged && !isOAuth) {
        if (!emailPassword) {
          addToast({ type: 'error', title: t('settings.passwordRequired'), message: t('settings.passwordRequiredMessage') });
          setIsSavingAccount(false);
          return;
        }
        await changeEmail(emailPassword, email);
        setEmailPassword('');
      }

      if (nameChanged) {
        await updateDisplayName(displayName);
      }

      if (nameChanged || emailChanged) {
        addToast({ type: 'success', title: t('settings.accountUpdated'), message: t('settings.accountUpdatedMessage') });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('settings.updateFailed');
      addToast({ type: 'error', title: t('settings.updateFailed'), message });
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleChangePassword = async () => {
    const errors: { currentPassword?: string; newPassword?: string; confirmPassword?: string } = {};
    if (!currentPassword) {
      errors.currentPassword = t('settings.currentPasswordRequired');
    }
    if (newPassword.length < 6) {
      errors.newPassword = t('settings.passwordMin6');
    }
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = t('settings.passwordsDoNotMatch');
    }
    setPasswordErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSavingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors({});
      addToast({ type: 'success', title: t('settings.passwordUpdated'), message: t('settings.passwordUpdatedMessage') });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('settings.passwordChangeFailed');
      addToast({ type: 'error', title: t('settings.passwordChangeFailed'), message });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount(deletePassword);
      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('settings.deleteFailed');
      addToast({ type: 'error', title: t('settings.deleteFailed'), message });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">{t('settings.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400">{t('settings.subtitle')}</p>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* Account Section */}
        <Card variant="default" padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-lime-100 dark:bg-lime-900/30 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-lime-700 dark:text-lime-400" />
            </div>
            <CardTitle>{t('settings.account')}</CardTitle>
          </div>

          <div className="space-y-4">
            <Input
              label={t('settings.displayName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />

            <div>
              <div className="flex items-center gap-2 mb-2">
                <label htmlFor="settings-email" className="block text-sm font-medium text-dark-700 dark:text-gray-300">{t('settings.emailLabel')}</label>
                {providerLabel && <Badge variant="lavender" size="sm">{providerLabel}</Badge>}
              </div>
              {isOAuth ? (
                <Input
                  id="settings-email"
                  value={email}
                  disabled
                />
              ) : (
                <>
                  <Input
                    id="settings-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    type="email"
                  />
                  {email !== (user?.email || '') && (
                    <div className="mt-3">
                      <Input
                        label={t('settings.currentPasswordForEmail')}
                        type="password"
                        value={emailPassword}
                        onChange={(e) => setEmailPassword(e.target.value)}
                        placeholder={t('settings.enterCurrentPassword')}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="pt-2">
              <Button
                variant="primary"
                onClick={handleSaveAccount}
                isLoading={isSavingAccount}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Username Section */}
        <Card variant="default" padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-lavender-100 dark:bg-lavender-900/30 rounded-xl flex items-center justify-center">
              <AtSign className="w-5 h-5 text-lavender-700 dark:text-lavender-400" />
            </div>
            <CardTitle>{t('settings.usernameSection')}</CardTitle>
          </div>

          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('settings.currentUsername')}: <span className="font-medium text-dark-800 dark:text-white">klipr.it/@{currentUsername}</span>
          </p>

          <div className="space-y-4">
            <Input
              label={t('settings.newUsername')}
              value={newUsername}
              onChange={(e) => handleUsernameChange(e.target.value)}
              placeholder={currentUsername}
              hint={t('settings.usernameHint')}
              error={usernameError}
            />

            <div className="pt-2">
              <Button
                variant="primary"
                onClick={handleUpdateUsername}
                isLoading={isSavingUsername}
                disabled={!newUsername || !!usernameError}
              >
                {t('settings.changeUsername')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Password Section — only for email/password users */}
        {!isOAuth && (
          <Card variant="default" padding="lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-lavender-100 dark:bg-lavender-900/30 rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5 text-lavender-700 dark:text-lavender-400" />
              </div>
              <CardTitle>{t('settings.passwordSection')}</CardTitle>
            </div>

            <div className="space-y-4">
              <Input
                label={t('settings.currentPassword')}
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t('settings.enterCurrentPassword')}
                error={passwordErrors.currentPassword}
              />
              <Input
                label={t('settings.newPassword')}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('settings.enterCurrentPassword')}
                error={passwordErrors.newPassword}
              />
              <Input
                label={t('settings.confirmPassword')}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('settings.confirmPassword')}
                error={passwordErrors.confirmPassword}
              />

              <div className="pt-2">
                <Button
                  variant="primary"
                  onClick={handleChangePassword}
                  isLoading={isSavingPassword}
                >
                  {t('settings.updatePassword')}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Language Section */}
        <Card variant="default" padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-700 dark:text-blue-400" />
            </div>
            <CardTitle>{t('settings.language')}</CardTitle>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{t('settings.languageDescription')}</p>
          <div className="flex flex-wrap gap-3">
            {[
              { code: 'en', label: 'settings.english' },
              { code: 'fr', label: 'settings.french' },
              { code: 'es', label: 'settings.spanish' },
              { code: 'it', label: 'settings.italian' },
            ].map(({ code, label }) => (
              <button
                key={code}
                onClick={() => i18n.changeLanguage(code)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  i18n.language === code || i18n.language.startsWith(`${code}-`)
                    ? 'bg-lime-400 text-dark-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300 dark:hover:bg-dark-600'
                }`}
              >
                {t(label)}
              </button>
            ))}
          </div>
        </Card>

        {/* Appearance Section */}
        <Card variant="default" padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <Sun className="w-5 h-5 text-purple-700 dark:text-purple-400" />
            </div>
            <CardTitle>{t('settings.appearance')}</CardTitle>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{t('settings.appearanceDescription')}</p>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setTheme('light')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                theme === 'light'
                  ? 'bg-lime-400 text-dark-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300 dark:hover:bg-dark-600'
              }`}
            >
              <Sun className="w-4 h-4" />
              {t('settings.lightMode')}
            </button>
            <button
              onClick={() => setTheme('dark')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                theme === 'dark'
                  ? 'bg-lime-400 text-dark-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300 dark:hover:bg-dark-600'
              }`}
            >
              <Moon className="w-4 h-4" />
              {t('settings.darkMode')}
            </button>
            <button
              onClick={() => setTheme('system')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                theme === 'system'
                  ? 'bg-lime-400 text-dark-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300 dark:hover:bg-dark-600'
              }`}
            >
              <Monitor className="w-4 h-4" />
              {t('settings.systemMode')}
            </button>
          </div>
        </Card>

        {/* Danger Zone */}
        <Card variant="default" padding="lg" className="border-red-200 dark:border-red-900/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="!text-red-600 dark:!text-red-400">{t('settings.dangerZone')}</CardTitle>
          </div>

          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('settings.deleteAccountWarning')}
          </p>

          <Button
            variant="ghost"
            className="!text-red-600 hover:!bg-red-50"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            {t('settings.deleteAccount')}
          </Button>
        </Card>
      </div>

      {/* Delete Account Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletePassword('');
        }}
        title={t('settings.deleteAccountTitle')}
        description={t('settings.deleteAccountDescription')}
        size="sm"
      >
        {!isOAuth && (
          <Input
            label={t('settings.enterPasswordToConfirm')}
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            placeholder="Your password"
          />
        )}
        {isOAuth && (
          <p className="text-gray-500 dark:text-gray-400">{t('settings.deleteAccountOAuthConfirm')}</p>
        )}
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setIsDeleteModalOpen(false);
              setDeletePassword('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="ghost"
            className="!text-red-600 hover:!bg-red-50"
            onClick={handleDeleteAccount}
            isLoading={isDeleting}
          >
            {t('settings.deleteAccount')}
          </Button>
        </ModalFooter>
      </Modal>

    </DashboardLayout>
  );
};

export default Settings;
